import React, { useRef, useEffect, useState } from 'react';
import * as faceapi from '@vladmandic/face-api';
import { Scan } from 'lucide-react';
import { initFaceApi } from '../utils/faceApiInit';
import { useNavigate } from 'react-router-dom';

const FacialRecognition = ({ cards, onMatch, onClose, onCreateCard, className = '' }) => {
  const navigate = useNavigate();
  const videoRef = useRef();
  const canvasRef = useRef();
  const intervalRef = useRef();
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchStatus, setSearchStatus] = useState('waiting'); // 'waiting', 'searching', 'matched', 'no-match'
  const [capturedImage, setCapturedImage] = useState(null);

  useEffect(() => {
    loadModels();
    console.log('Cards available for matching:', cards.filter(card => card.faceDescriptor).length);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      stopVideo();
      setScanning(false);
      setSearchStatus('waiting');
    };
  }, [cards]);

  const loadModels = async () => {
    try {
      setLoading(true);
      const initialized = await initFaceApi();
      if (initialized) {
        setModelsLoaded(true);
        console.log('Face detection models loaded successfully');
      }
    } catch (error) {
      console.error('Error loading models:', error);
    } finally {
      setLoading(false);
    }
  };

  const startVideo = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use rear camera
      });
      videoRef.current.srcObject = stream;
      setScanning(true);
      setSearchStatus('searching');
    } catch (error) {
      console.error('Error accessing webcam:', error);
    }
  };

  const stopVideo = () => {
    const stream = videoRef.current?.srcObject;
    stream?.getTracks().forEach(track => track.stop());
    setScanning(false);
  };

  const handleVideoOnPlay = async () => {
    if (!modelsLoaded) {
      console.log('Models not loaded yet, attempting to load...');
      await loadModels();
      if (!modelsLoaded) {
        console.error('Failed to load models');
        return;
      }
    }

    // Clear any existing canvas
    while (canvasRef.current?.firstChild) {
      canvasRef.current.removeChild(canvasRef.current.firstChild);
    }

    const canvas = faceapi.createCanvasFromMedia(videoRef.current);
    canvas.style.position = 'absolute';
    canvas.style.left = '0';
    canvas.style.top = '0';
    
    // Force canvas dimensions to match video
    const videoEl = videoRef.current;
    canvas.width = videoEl.offsetWidth;
    canvas.height = videoEl.offsetHeight;
    
    canvasRef.current.appendChild(canvas);

    const displaySize = {
      width: videoEl.offsetWidth,
      height: videoEl.offsetHeight
    };
    
    faceapi.matchDimensions(canvas, displaySize);

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(async () => {
      if (!scanning) {
        console.log('Not scanning, returning early');
        return;
      }

      try {
        console.log('Attempting face detection...');
        const detections = await faceapi
          .detectAllFaces(videoRef.current, new faceapi.TinyFaceDetectorOptions())
          .withFaceLandmarks()
          .withFaceDescriptors();

        console.log('Detections:', detections.length);

        // Clear the canvas and draw the new detections
        const canvas = canvasRef.current.firstChild;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (detections.length > 0) {
          console.log('Face detected, drawing box...');
          const resizedDetections = faceapi.resizeResults(detections, {
            width: videoRef.current.offsetWidth,
            height: videoRef.current.offsetHeight
          });

          // Draw the face box
          faceapi.draw.drawDetections(canvas, resizedDetections);
          
          // Existing face matching code...
          const faceDescriptor = detections[0].descriptor;
          let bestMatch = null;
          let bestDistance = 0.45;
          
          // Add console logs for debugging
          console.log('Checking against', cards.length, 'cards');
          for (const card of cards) {
            if (card.faceDescriptor && !card.deleted) {
              const distance = faceapi.euclideanDistance(
                faceDescriptor,
                new Float32Array(Object.values(card.faceDescriptor))
              );
              console.log('Distance for card:', card.firstName, distance);
              
              if (distance < 0.45 && (!bestMatch || distance < bestDistance)) {
                bestMatch = { card, distance };
                bestDistance = distance;
              }
            }
          }

          if (bestMatch) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            stopVideo();
            setSearchStatus('matched');
            
            setTimeout(() => {
              onMatch(bestMatch.card);
            }, 1000);
            
            return bestMatch.card;
          }
        }
      } catch (error) {
        console.error('Error during face detection:', error);
      }
    }, 100);
  };

  const handleScan = async () => {
    if (!modelsLoaded) return;
    
    if (scanning) {
      stopVideo();
    } else {
      await startVideo();
    }
  };

  const findMatch = (descriptor, threshold = 0.45) => {
    setSearchStatus('searching');
    const matches = cards
      .filter(card => card.faceDescriptor && !card.deleted)
      .map(card => ({
        card,
        distance: faceapi.euclideanDistance(descriptor, new Float32Array(card.faceDescriptor))
      }));

    if (matches.length === 0) {
      setSearchStatus('searching');
      return null;
    }

    const bestMatch = matches.reduce((best, current) => 
      current.distance < best.distance ? current : best
    , matches[0]);

    if (bestMatch.distance <= threshold) {
      // Stop scanning immediately
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      stopVideo();
      setSearchStatus('matched');
      
      // Show match message for 1 second then trigger navigation
      setTimeout(() => {
        onMatch(bestMatch.card);
      }, 1000);
      
      return bestMatch.card;
    }
    
    setSearchStatus('searching');
    return null;
  };

  const captureFrame = () => {
    if (!videoRef.current) return null;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    
    return canvas.toDataURL('image/jpeg');
  };

  const handleCreateCard = () => {
    const imageData = captureFrame();
    if (imageData) {
      onCreateCard(imageData);
      stopVideo();
      onClose();
    }
  };

  return (
    <div className="relative">
      <div className="flex flex-col gap-4 mb-4">
        <div className="flex gap-4">
          <button
            onClick={handleScan}
            className="flex items-center justify-center gap-2 px-4 py-2 text-lg font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <Scan className="w-5 h-5" />
            Start Face Recognition*
          </button>
          <button
            onClick={onClose}
            className="flex items-center justify-center gap-2 px-4 py-2 text-lg font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Back to Cards
          </button>
        </div>
        <p className="text-sm text-gray-500 italic">
          * Face recognition technology may produce unexpected or inaccurate results
        </p>
      </div>

      {/* Video overlay */}
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 ${scanning ? '' : 'hidden'}`}>
        <div style={{ position: 'absolute', left: '50%', top: '80px', transform: 'translateX(-50%)' }} className="bg-white rounded-lg flex flex-col max-w-[90vw] w-[640px] min-h-[60vh] max-h-[90vh] p-4">
          {/* Status message */}
          <div className="text-center text-gray-700 font-medium mb-2">
            {searchStatus === 'searching' && 'Looking for a match...'}
            {searchStatus === 'matched' && (
              <div className="bg-green-500 px-4 py-2 rounded-lg inline-block text-white">
                Match found! Redirecting...
              </div>
            )}
          </div>
          {/* Video container */}
          <div className="flex-1 flex items-center justify-center w-full bg-gray-100 rounded-lg shadow-md p-4 my-2 min-h-[200px]">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              onPlay={handleVideoOnPlay}
              className="max-w-full max-h-full object-contain rounded-lg"
              style={{ aspectRatio: '4/3' }}
            />
            <div ref={canvasRef} className="absolute top-0 left-0" />
          </div>
          {/* Buttons */}
          <div className="flex justify-center gap-4 mt-4 w-full">
            <button
              onClick={handleScan}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              disabled={searchStatus === 'matched'}
            >
              {scanning ? 'Stop Searching' : 'Start Searching'}
            </button>
            <button
              onClick={handleCreateCard}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              disabled={searchStatus === 'matched'}
            >
              Create New Card
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacialRecognition;